import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Alert,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Collapse,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import api from "../../api/api";
import { ThemeContext } from "../context/ThemeContext";
import { useNavigate } from "react-router-dom";

function AdminPanel() {
  const navigate = useNavigate();
  const { setTheme } = useContext(ThemeContext);
  const [businessStats, setBusinessStats] = useState([]);
  const [systemErrors, setSystemErrors] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState([]);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    setLoadErrors([]);
    try {
      await Promise.all([
        fetchBusinessStats(),
        fetchSystemErrors(),
        checkSystemHealth(),
      ]);
    } catch (error) {
      console.error("Ошибка загрузки данных админ-панели:", error);
    } finally {
      setLoading(false);
    }
  };

  // Загрузка статистики по бизнесам
  const fetchBusinessStats = async () => {
    try {
      const response = await api.get("/admin/business-stats/");
      console.log("📊 Ответ от API:", response.data);

      // API возвращает объект с полем businesses
      if (Array.isArray(response.data?.businesses)) {
        setBusinessStats(response.data.businesses);
      } else if (Array.isArray(response.data)) {
        setBusinessStats(response.data);
      } else {
        console.warn("Неожиданный формат данных:", response.data);
        setBusinessStats([]);
      }
    } catch (error) {
      console.error("Ошибка загрузки статистики бизнесов:", error);
      setBusinessStats([]);
      setLoadErrors(errors => [...errors, "Не удалось загрузить статистику бизнесов"]);
    }
  };

  // Проверка системных ошибок
  const fetchSystemErrors = async () => {
    try {
      const response = await api.get("/admin/system-errors/");
      setSystemErrors(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Ошибка загрузки системных ошибок:", error);
      setSystemErrors([]);
      setLoadErrors(errors => [...errors, "Не удалось загрузить системные уведомления"]);
    }
  };

  // Проверка здоровья системы
  const checkSystemHealth = async () => {
    try {
      const response = await api.get("/health");
      setSystemHealth({
        status: ["healthy", "ok"].includes(response.data?.status) ? "healthy" : (response.data?.status || "unknown"),
        database: response.data?.database || "unknown",
        uptime: response.data?.uptime || "unknown",
        last_check: new Date().toLocaleTimeString(),
      });
    } catch (error) {
      setSystemHealth({
        status: "error",
        database: "disconnected",
        uptime: "unknown",
        last_check: new Date().toLocaleTimeString(),
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: "RUB",
    }).format(amount);
  };

  const getErrorSeverityColor = (severity) => {
    switch (severity) {
      case "error":
        return "error";
      case "warning":
        return "warning";
      case "info":
        return "info";
      default:
        return "info";
    }
  };

  const getSubscriptionStatusColor = (status) => {
    switch (status) {
      case "активна":
        return "success";
      case "скоро истечет":
        return "warning";
      case "критично":
        return "error";
      case "истекла":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Загрузка панели администратора...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Заголовок */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          <DashboardIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Панель Администратора
        </Typography>
        <Tooltip title="Обновить данные">
          <IconButton onClick={loadAdminData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {loadErrors.map(message => <Alert key={message} severity="error">{message}</Alert>)}
      {/* Статус системы */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Статус системы
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Chip
                  label={`Система: ${systemHealth?.status || "Неизвестно"}`}
                  color={
                    systemHealth?.status === "healthy" ? "success" : "error"
                  }
                  variant="outlined"
                />
                <Chip
                  label={`БД: ${systemHealth?.database || "Неизвестно"}`}
                  color={
                    systemHealth?.database === "connected" ? "success" : "error"
                  }
                  variant="outlined"
                />
                <Chip
                  label={`Uptime: ${systemHealth?.uptime || "Неизвестно"}`}
                  color="info"
                  variant="outlined"
                />
                <Chip
                  label={`Проверено: ${systemHealth?.last_check || "Никогда"}`}
                  color="default"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Системные ошибки */}
      {systemErrors.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ErrorIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Системные уведомления ({systemErrors.length})
                </Typography>
                {systemErrors.map((error) => (
                  <Alert
                    key={error.id}
                    severity={getErrorSeverityColor(error.severity)}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body2">
                      <strong>{error.timestamp}</strong> - {error.message}
                    </Typography>
                  </Alert>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}{" "}
      {/* Список арендных бизнесов в виде аккордеона */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            <BusinessIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Арендные бизнесы ({businessStats.length})
          </Typography>

          {businessStats.map((business) => (
            <Accordion
              key={business.id}
              sx={{
                mb: 1,
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: "8px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${business.id}-content`}
                id={`panel${business.id}-header`}
                sx={{
                  backgroundColor: "rgba(0, 0, 0, 0.03)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    pr: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {business.name}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Chip
                      label={
                        business.status === "active" ? "Активен" : "Неактивен"
                      }
                      color={
                        business.status === "active" ? "success" : "default"
                      }
                      size="small"
                    />
                    <Chip
                      label={`${business.clients_count || 0} клиентов`}
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label={`${business.assets_count || 0} активов`}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      Основная информация
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography color="text.secondary">
                        <CalendarIcon
                          sx={{ mr: 1, verticalAlign: "middle", fontSize: 16 }}
                        />
                        Создан:{" "}
                        {business.created_at
                          ? new Date(business.created_at).toLocaleDateString(
                              "ru-RU"
                            )
                          : "Дата неизвестна"}
                      </Typography>
                      <Typography color="text.secondary">
                        <PersonIcon
                          sx={{ mr: 1, verticalAlign: "middle", fontSize: 16 }}
                        />
                        Контакт: {business.contact_person || "Не указано"}
                      </Typography>
                      <Typography color="text.secondary">
                        <EmailIcon
                          sx={{ mr: 1, verticalAlign: "middle", fontSize: 16 }}
                        />
                        Email: {business.email || "Не указано"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      Статистика
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <GroupIcon color="primary" />
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {business.clients_count || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Клиентов
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <PersonIcon color="secondary" />
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {business.staff_count || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Сотрудников
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <InventoryIcon color="info" />
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {business.assets_count || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Активов
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <BusinessIcon color="secondary" />
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {business.active_deals || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Сделок
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold" }}
                        >
                          Подписка:
                          <Chip
                            label={business.subscription_status || "Не указана"}
                            color={getSubscriptionStatusColor(
                              business.subscription_status?.toLowerCase() ||
                                "неизвестно"
                            )}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Осталось дней: {business.subscription_days_left || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: "bold", color: "success.main" }}
                        >
                          {formatCurrency(business.monthly_revenue || 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Выручка за месяц
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      </Grid>
    </Container>
  );
}

export default AdminPanel;
